import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "../../supabaseClient";
import { getPublicImageUrl } from "../../helpers/helpers";

const INCENTIVE_LABELS = {
  free_guestlist: "Free Guest List",
  service_fee_reduction: "No Service Fee",
  free_spin_ticket: "Free Spin & Win Ticket",
  free_drink: "Free Drink",
  discount_code: "Exclusive Discount",
  custom: "",
};

const INCENTIVE_ICONS = {
  free_guestlist: "M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z",
  service_fee_reduction: "M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z",
  free_spin_ticket: "M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z",
  free_drink: "M21 12a9 9 0 11-18 0 9 9 0 0118 0zM15.91 11.672a.375.375 0 010 .656l-5.603 3.113a.375.375 0 01-.557-.328V8.887c0-.286.307-.466.557-.327l5.603 3.112z",
  discount_code: "M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z",
};

export default function IncentiveOfferPopup({ config, onDismiss, onSubmit, submitting, isLoggedIn }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [emailConsent, setEmailConsent] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(config.countdown_seconds || 0);
  const [couponCopied, setCouponCopied] = useState(false);

  const incentiveLabel = useMemo(() => {
    if (config.incentive_type === "custom") return config.incentive_value || "Special Offer";
    return INCENTIVE_LABELS[config.incentive_type] || config.incentive_value || "Special Offer";
  }, [config.incentive_type, config.incentive_value]);

  const incentiveIcon = INCENTIVE_ICONS[config.incentive_type] || "";

  // Countdown timer
  useEffect(() => {
    if (!config.countdown_seconds || submitted) return;
    setTimeLeft(config.countdown_seconds);
    const id = setInterval(() => {
      setTimeLeft((p) => {
        if (p <= 1) { clearInterval(id); return 0; }
        return p - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [config.countdown_seconds, submitted]);

  const canSubmit = () => {
    if (config.show_email_field && !email.trim()) return false;
    if (config.show_phone_field && !phone.trim()) return false;
    if (config.show_name_field && !name.trim()) return false;
    if (config.show_email_field && config.email_consent_text && !emailConsent) return false;
    if (config.show_phone_field && config.sms_consent_text && !smsConsent) return false;
    return true;
  };

  const handleSubmit = async () => {
    if (!canSubmit()) return;
    const data = { name, email, phone };
    const result = await onSubmit(data);
    if (result !== false) {
      setSubmitted(true);
      if (config.coupon_code) {
        try {
          await navigator.clipboard.writeText(config.coupon_code);
          setCouponCopied(true);
        } catch {}
      }
      if (config.redirect_url) {
        setTimeout(() => { window.location.href = config.redirect_url; }, 2500);
      }
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        key="popup-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[999999] p-4"
        onClick={(e) => { if (e.target === e.currentTarget) onDismiss(); }}
      >
        <motion.div
          key="popup-card"
          initial={{ scale: 0.92, opacity: 0, y: 30 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.92, opacity: 0, y: 30 }}
          transition={{ type: "spring", duration: 0.5, bounce: 0.3 }}
          className="relative bg-[#17121f] border border-[#26202e] rounded-2xl w-[380px] max-w-[calc(100vw-32px)] flex flex-col items-center overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Background effects */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-2xl">
            <div
              className="absolute bottom-0 left-0 right-0 h-[400px]"
              style={{ backgroundImage: "linear-gradient(270deg, #17121f 2.2%, #590fc0 31.5%, #75018c 68%, #17121f 97.2%)" }}
            />
            <div className="absolute inset-0 backdrop-blur-[50px] bg-[rgba(11,4,21,0.5)]" />
          </div>

          {/* Close */}
          <button
            onClick={onDismiss}
            className="absolute top-3 right-3 z-20 text-white/40 hover:text-white transition-colors p-1"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {submitted ? (
            <div className="relative z-10 flex flex-col items-center gap-4 py-10 px-6 text-center w-full">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", delay: 0.1 }}
                className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center"
              >
                <svg className="w-8 h-8 text-green-400" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
              </motion.div>
              <h3 className="text-white text-xl font-semibold">
                {config.success_message || `You got ${incentiveLabel}!`}
              </h3>
              {config.incentive_description && (
                <p className="text-white/60 text-sm">{config.incentive_description}</p>
              )}
              {config.coupon_code && (
                <div className="mt-2 bg-purple-600/20 border border-purple-500/30 rounded-lg px-5 py-3 flex items-center gap-3">
                  <span className="text-purple-300 font-mono font-bold text-lg tracking-wider">{config.coupon_code}</span>
                  <span className="text-green-400 text-xs font-medium">
                    {couponCopied ? "Copied!" : ""}
                  </span>
                </div>
              )}
              {config.redirect_url && (
                <p className="text-white/40 text-xs mt-1">Redirecting you...</p>
              )}
            </div>
          ) : (
            <>
              {/* Header with incentive badge */}
              <div className="relative z-10 w-full pt-6 px-6 pb-0">
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex justify-center mb-4"
                >
                  <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-gradient-to-r from-[#7e25f7]/30 to-[#590fc0]/30 border border-[#7e25f7]/50">
                    {incentiveIcon && (
                      <svg className="w-4 h-4 text-[#b98ef5] mr-1.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d={incentiveIcon} />
                      </svg>
                    )}
                    <span className="text-[13px] font-bold text-[#c9a9ff] tracking-wide uppercase">
                      {incentiveLabel}
                    </span>
                  </div>
                </motion.div>
                <h2 className="text-[22px] font-semibold text-white text-center leading-tight">
                  {config.title}
                </h2>
                {config.subtitle && (
                  <p className="text-white/50 text-[14px] text-center mt-1.5">{config.subtitle}</p>
                )}
              </div>

              {/* Form */}
              <div className="relative z-10 w-full px-6 pt-4 pb-1 space-y-3">
                {config.show_name_field && (
                  <input
                    className="w-full bg-white/5 border border-white/10 focus:border-purple-500/60 rounded-lg px-4 py-3 text-white text-[14px] placeholder-white/30 outline-none transition-all"
                    placeholder="Your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                )}
                {config.show_email_field && (
                  <input
                    className="w-full bg-white/5 border border-white/10 focus:border-purple-500/60 rounded-lg px-4 py-3 text-white text-[14px] placeholder-white/30 outline-none transition-all"
                    placeholder="Email address"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                )}
                {config.show_phone_field && (
                  <input
                    className="w-full bg-white/5 border border-white/10 focus:border-green-500/60 rounded-lg px-4 py-3 text-white text-[14px] placeholder-white/30 outline-none transition-all"
                    placeholder="Phone number"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                )}

                {/* Consent checkboxes */}
                {config.show_email_field && config.email_consent_text && (
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={emailConsent}
                      onChange={(e) => setEmailConsent(e.target.checked)}
                      className="mt-0.5 w-4 h-4 rounded border-white/20 bg-white/10 text-purple-500 focus:ring-purple-500"
                    />
                    <span className="text-white/40 text-[11px] leading-relaxed">{config.email_consent_text}</span>
                  </label>
                )}
                {config.show_phone_field && config.sms_consent_text && (
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={smsConsent}
                      onChange={(e) => setSmsConsent(e.target.checked)}
                      className="mt-0.5 w-4 h-4 rounded border-white/20 bg-white/10 text-purple-500 focus:ring-purple-500"
                    />
                    <span className="text-white/40 text-[11px] leading-relaxed">{config.sms_consent_text}</span>
                  </label>
                )}

                {/* Social proof */}
                {config.show_social_proof && config.social_proof_count > 0 && (
                  <div className="flex items-center justify-center gap-2 pt-1">
                    <div className="flex -space-x-1.5">
                      {[..."123"].map((_, i) => (
                        <div key={i} className="w-5 h-5 rounded-full bg-gradient-to-b from-purple-400 to-purple-600 border border-[#17121f] flex items-center justify-center">
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M10 9a3 3 0 100-6 3 3 0 000 6zM6 8a2 2 0 11-4 0 2 2 0 014 0zM1.49 15.326a.78.78 0 01-.358-.442 3 3 0 014.308-3.516 6.484 6.484 0 00-1.905 3.959c-.023.222-.014.444.025.663a3.003 3.003 0 010 .002L5.56 16l-4.07-.674zM18.44 15.326a.78.78 0 00.358-.442 3 3 0 00-4.308-3.516 6.484 6.484 0 011.905 3.959c.023.222.014.444-.025.663h-4.07l2.07.326zM14 8a2 2 0 11-4 0 2 2 0 014 0zM12.256 12c.448.004.895.039 1.335.098a5.003 5.003 0 011.258 4.524.75.75 0 01-.575.556L14 17.21l-2.487-.316a3 3 0 01-.273-.564 2.995 2.995 0 01-.24-.83V12h1.256z" />
                          </svg>
                        </div>
                      ))}
                    </div>
                    <span className="text-white/50 text-[11px]">
                      <strong className="text-white/70">{config.social_proof_count}+</strong> people claimed this offer
                    </span>
                  </div>
                )}
              </div>

              {/* CTA + urgency */}
              <div className="relative z-10 w-full px-6 pt-2 pb-6 space-y-3">
                {config.countdown_seconds && timeLeft > 0 && (
                  <div className="flex items-center justify-center gap-2">
                    <svg className="w-4 h-4 text-amber-400 animate-pulse" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-amber-400 text-[12px] font-medium">
                      Offer expires in {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, "0")}
                    </span>
                  </div>
                )}
                <button
                  onClick={handleSubmit}
                  disabled={submitting || !canSubmit()}
                  className="w-full bg-gradient-to-b from-[#7e25f7] to-[#590fc0] border border-[#7e25f7] text-white font-bold text-[15px] py-3.5 px-6 rounded-xl transition-all duration-200 hover:brightness-110 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100 shadow-lg shadow-purple-600/30 flex items-center justify-center gap-2"
                >
                  {submitting && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  )}
                  {config.button_text || "Claim Offer"}
                </button>
                <button
                  onClick={onDismiss}
                  className="w-full text-center text-[13px] text-white/40 hover:text-white/70 transition-colors py-1"
                >
                  No thanks
                </button>
              </div>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
